import type { ReactNode } from 'react';
import { href } from '@/lib/paths.ts';
import { SplitText } from './GradientText.tsx';
import { Img } from './Img.tsx';
import { Layout } from './Layout.tsx';
import { ButtonLink, Eyebrow, Icons } from './ui.tsx';

export interface ArticleMeta {
  readonly source: string;
  readonly date: string;
  readonly author?: string;
  readonly title: string;
  readonly lead: string;
  readonly image: string;
  readonly imageAlt: string;
  readonly imageWidth: number;
  readonly imageHeight: number;
  readonly sourceUrl: string;
  readonly disclaimer: string;
}

/**
 * Shared chrome for the two syndicated article pages: back link, meta row,
 * hero, body slot, source disclaimer, and the bottom CTA.
 */
export function ArticleLayout({
  meta,
  children,
}: {
  meta: ArticleMeta;
  children: ReactNode;
}) {
  return (
    // currentPath is intentionally empty: article pages have no nav entry of
    // their own, so no link is marked active — matching the original site.
    <Layout currentPath="" navOnLight>
      <article>
        <div className="article-hero">
          <div className="container">
            <a href={href('/newsroom')} className="article-nav-back">
              {Icons.arrowLeft}
              News Room
            </a>
            <div className="article-meta">
              <span className="article-meta__source">{meta.source}</span>
              <span className="article-meta__dot" aria-hidden="true" />
              <span className="article-meta__date">{meta.date}</span>
              {meta.author && (
                <>
                  <span className="article-meta__dot" aria-hidden="true" />
                  <span className="article-meta__date">By {meta.author}</span>
                </>
              )}
            </div>
            <h1 className="fade-in">{meta.title}</h1>
            <p className="lead fade-in fade-in-delay-1">{meta.lead}</p>
            <div className="article-featured-image fade-in fade-in-delay-2">
              <Img
                src={meta.image}
                alt={meta.imageAlt}
                width={meta.imageWidth}
                height={meta.imageHeight}
                priority
                sizes="(max-width: 820px) 100vw, 780px"
              />
            </div>
          </div>
        </div>

        <div className="container">
          <div className="article-body fade-in">
            {children}

            <div className="article-disclaimer">
              {meta.disclaimer}
              <br />
              <a
                href={meta.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="article-source-link"
              >
                View Original Article
                <span className="sr-only"> (opens in a new tab)</span>
                {Icons.externalLink}
              </a>
            </div>
          </div>
        </div>
      </article>

      <section className="cta-section section-pad" style={{ marginTop: 0 }}>
        <div className="container">
          <Eyebrow className="fade-in">Work With Us</Eyebrow>
          <h2 className="fade-in fade-in-delay-1">
            <SplitText>Ready to Get Started?</SplitText>
          </h2>
          <p className="lead fade-in fade-in-delay-2">
            Schedule a consultation and discover how Lux Financial can help
            optimize your tax strategy.
          </p>
          <div className="cta-buttons fade-in fade-in-delay-3">
            <ButtonLink to="/#contact">Schedule a Consultation</ButtonLink>
            <ButtonLink to="/newsroom" variant="outline">
              Back to News Room
            </ButtonLink>
          </div>
        </div>
      </section>
    </Layout>
  );
}

/** Pull quote with its attribution line, as used in both articles. */
export function PullQuote({
  children,
  attribution,
}: {
  children: string;
  attribution: string;
}) {
  return (
    <figure>
      <blockquote>{children}</blockquote>
      <figcaption className="article-body__attribution">
        {attribution}
      </figcaption>
    </figure>
  );
}
